import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { ZagIcon } from '../icon';

/**
 * Absence pill — the shared visual for a booked absence.
 *
 * The `<zag-absence-pill>` host element itself is the pill (border, bg,
 * radius), so consumers can bind tooltip / click / keydown handlers directly
 * on the tag. Content projected into `[slot=actions]` renders as trailing
 * action chips.
 *
 * Colour mixing: the absence type's `--absence-pill-color` is mixed 18% over
 * the surface for background, 30% for border, 65% for text — matches what the
 * planning grid used before this was extracted, so every visual instance
 * stays in lockstep with a token change.
 */
@Component({
  selector: 'zag-absence-pill',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ZagIcon],
  template: `
    <span [zagIcon]="icon()" aria-hidden="true" class="zag-absence-pill__icon"></span>
    <span class="zag-absence-pill__label">{{ label() }}</span>
    <span class="zag-absence-pill__actions">
      <ng-content select="[slot=actions]" />
    </span>
  `,
  styleUrl: './absence-pill.scss',
  host: {
    class: 'zag-absence-pill',
    '[class.zag-absence-pill--interactive]': 'interactive()',
    '[style.--absence-pill-color]': 'color()',
  },
})
export class AbsencePill {
  readonly label = input.required<string>();
  readonly icon = input<string>('event_busy');
  readonly color = input<string | null>(null);
  /** Adds pointer cursor + subtle hover — set when the host handles clicks. */
  readonly interactive = input<boolean>(false);
}
